import React, { useEffect, useRef } from "react";
import { useContent } from "../context/ContentContext";

/**
 * Renders `field` content, editable inline when admin has toggled Edit Mode.
 * Falls back to `children` (a string OR a node that resolves to text).
 *
 * Usage:
 *   <Editable field="company.address" as="span">{company.address}</Editable>
 *   <Editable field="hero.slide.0.title" as="h1" className="...">Fallback title</Editable>
 */
export default function Editable({ field, children, as: Tag = "span", className, multiline = false, style, ...rest }) {
  const { t, update, editMode, isAdmin } = useContent();
  const ref = useRef(null);
  const fallback = typeof children === "string" ? children : (children?.props?.children ?? "");
  const value = t(field, fallback);
  const canEdit = isAdmin && editMode;

  // Sync current value into the DOM only when NOT actively editing
  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement === ref.current) return;
    if (ref.current.textContent !== value) ref.current.textContent = value || "";
  }, [value]);

  const onBlur = (e) => {
    const next = (e.currentTarget.textContent || "").trim();
    if (next !== value) update(field, next);
  };

  const editableProps = canEdit
    ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        onBlur,
        onKeyDown: (e) => {
          if (!multiline && e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === "Escape") { e.preventDefault(); e.currentTarget.textContent = value || ""; e.currentTarget.blur(); }
        },
        "data-editable": field,
        title: `Editing: ${field}  (Esc to cancel · Enter to save)`,
        style: {
          outline: "2px dashed rgba(45,106,79,0.55)",
          outlineOffset: "3px",
          borderRadius: "4px",
          padding: "0 2px",
          cursor: "text",
          ...(style || {}),
        },
      }
    : { style };

  return (
    <Tag ref={ref} className={className} {...editableProps} {...rest}>
      {value}
    </Tag>
  );
}
